import { z } from "zod";
import { workspaceFetch } from "@/lib/workspace-lifetime";
import { apiFetch, type ApiFetchOptions } from "@/lib/api";

export const workspaceSchema = z.object({
  organization_id: z.string().uuid(), organization_name: z.string(), execution_epoch: z.number().int().nonnegative(),
  tenant_id: z.string().uuid(), domain_id: z.string().uuid(),
  tenant_name: z.string(), domain_name: z.string(), operations: z.array(z.string()),
});
export type Workspace = z.infer<typeof workspaceSchema>;
export type WorkspaceIdentity = Pick<Workspace, "tenant_id" | "domain_id">;
export const tenantSchema = z.object({ id: z.string().uuid(), name: z.string(), is_active: z.boolean() });
export const personSchema = z.object({ id: z.string().uuid(), email: z.string(), name: z.string(), eligible_owner: z.boolean() });
export const domainSchema = z.object({
  id: z.string().uuid(), tenant_id: z.string().uuid(), name: z.string(), owner_id: z.string().uuid(),
  publication_state: z.string(), is_active: z.boolean(), classification_profile: z.string().nullable(),
  residency_profile: z.string().nullable(), retention_profile: z.string().nullable(),
});
export const ownedDomainSchema = workspaceSchema.omit({ operations: true, organization_id: true, organization_name: true, execution_epoch: true }).extend({
  publication_state: z.string(), is_active: z.boolean(),
});
export const memberSchema = z.object({ user_id: z.string().uuid(), name: z.string(), email: z.string() });
export const grantSchema = z.object({
  grant_id: z.string().uuid(), subject_id: z.string().uuid(), granted_by: z.string().uuid(),
  reason: z.string(), valid_from: z.string(), expires_at: z.string().nullable(), revoked_at: z.string().nullable(),
  operations: z.array(z.string()), api_key_allowed: z.boolean(),
});
export type TenantMetadata = z.infer<typeof tenantSchema>;
export type PersonMetadata = z.infer<typeof personSchema>;
export type DomainMetadata = z.infer<typeof domainSchema>;
export type OwnedDomain = z.infer<typeof ownedDomainSchema>;
export type GrantMetadata = z.infer<typeof grantSchema>;
export type MemberMetadata = z.infer<typeof memberSchema>;

export class WorkspaceApiError extends Error {
  constructor(public status: number, public code: string) { super(code); }
}

async function request<T>(path: string, schema: z.ZodType<T>, options: ApiFetchOptions = {}): Promise<T> {
  const response = await apiFetch<unknown>(path, { cache: "no-store", ...options });
  if (!response.ok) {
    const error = z.object({ error: z.object({ code: z.string() }) }).safeParse(response.data);
    throw new WorkspaceApiError(response.status, error.success ? error.data.error.code : "REQUEST_FAILED");
  }
  const result = schema.safeParse(response.data);
  if (!result.success) throw new WorkspaceApiError(502, "INVALID_RESPONSE");
  return result.data;
}

export function workspaceHeaders(scope: WorkspaceIdentity): Record<string, string> {
  return { "X-Tenant-ID": z.string().uuid().parse(scope.tenant_id), "X-Domain-ID": z.string().uuid().parse(scope.domain_id) };
}
export const getWorkspaces = (offset = 0) => request(`/domain-access/workspaces?offset=${offset}&limit=100`, z.array(workspaceSchema));
export const getOwnedDomains = (offset = 0) => request(`/domain-access/owned-domains?offset=${offset}&limit=100`, z.array(ownedDomainSchema));
export const getTenants = (offset = 0) => request(`/tenants?offset=${offset}&limit=100`, z.array(tenantSchema));
export const createTenant = (name: string) => request("/tenants", tenantSchema, { method: "POST", body: JSON.stringify({ name }) });
export const searchPeople = (query: string) => request(`/tenants/people?query=${encodeURIComponent(query)}`, z.array(personSchema));
export const addTenantMember = (tenant: string, user: string) => request(`/tenants/${tenant}/members`, z.object({ user_id: z.string().uuid(), is_active: z.boolean() }), { method: "PUT", body: JSON.stringify({ user_id: user }) });
export const getTenantDomains = (tenant: string, offset = 0) => request(`/tenants/${tenant}/domains?offset=${offset}&limit=100`, z.array(domainSchema));
export type DomainShellInput = { name: string; owner_id: string; classification_profile: string; residency_profile: string; retention_profile: string };
export const createDomainShell = (tenant: string, payload: DomainShellInput) => request(`/tenants/${tenant}/domains`, domainSchema, { method: "POST", body: JSON.stringify(payload) });
const domainPath = (scope: WorkspaceIdentity) => `/domain-access/tenants/${scope.tenant_id}/domains/${scope.domain_id}`;
export const getDomainMembers = (scope: WorkspaceIdentity, offset = 0) => request(`${domainPath(scope)}/members?offset=${offset}&limit=100`, z.array(memberSchema));
export const getDomainGrants = (scope: WorkspaceIdentity, offset = 0) => request(`${domainPath(scope)}/grants?offset=${offset}&limit=100`, z.array(grantSchema));
export type GrantInput = { subject_id: string; operations: string[]; reason: string; expires_at: string; api_key_allowed: boolean; regrant_revocation_id?: string; regrant_epoch?: number };
export const issueDomainGrant = (scope: WorkspaceIdentity, payload: GrantInput) => request(`${domainPath(scope)}/grants`, z.object({ grant_id: z.string().uuid(), expires_at: z.string() }), { method: "POST", body: JSON.stringify(payload) });
export async function revokeDomainGrant(scope: WorkspaceIdentity, grant: string) {
  const result = await apiFetch(`${domainPath(scope)}/grants/${grant}`, { method: "DELETE" });
  if (!result.ok) throw new WorkspaceApiError(result.status, "REQUEST_FAILED");
}

export const domainSubjectStateSchema = z.object({
  subject_id: z.string().uuid(), phase: z.enum(["active", "revoking", "revoked"]),
  epoch: z.number().int().nonnegative(), revocation_id: z.string().uuid().nullable(),
});
export type DomainSubjectState = z.infer<typeof domainSubjectStateSchema>;
const domainRevocationSchema = z.object({
  revocation_id: z.string().uuid(), phase: z.enum(["revoking", "revoked"]),
  fence_committed: z.literal(true), status_available: z.boolean(),
  subject_id: z.string().uuid().optional(), epoch: z.number().int().nonnegative().optional(),
  pending_operations: z.number().int().nonnegative().optional(), external_outcome_unknown: z.boolean().optional(),
}).superRefine((value, context) => {
  if (value.phase === "revoked" && (!value.status_available || value.epoch === undefined || value.subject_id === undefined || value.pending_operations !== 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid completion" });
  }
});
export type DomainRevocation = z.infer<typeof domainRevocationSchema>;
const safeDomainPath = (scope: WorkspaceIdentity) => domainPath(workspaceSchema.pick({ tenant_id: true, domain_id: true }).parse(scope));
export const getDomainSubjectState = (scope: WorkspaceIdentity, subject: string) => request(
  `${safeDomainPath(scope)}/subjects/${z.string().uuid().parse(subject)}/access-state`, domainSubjectStateSchema);
async function revocationRequest(path: string, options: ApiFetchOptions = {}): Promise<DomainRevocation> {
  const response = await apiFetch<unknown>(path, { cache: "no-store", ...options });
  if (!response.ok) {
    const error = z.object({ error: z.object({ code: z.string() }) }).safeParse(response.data);
    throw new WorkspaceApiError(response.status, error.success ? error.data.error.code : "REQUEST_FAILED");
  }
  const parsed = domainRevocationSchema.safeParse(response.data);
  if (!parsed.success || response.status !== (parsed.data.phase === "revoked" ? 200 : 202)) throw new WorkspaceApiError(502, "INVALID_RESPONSE");
  return parsed.data;
}
export const revokeDomainSubject = (scope: WorkspaceIdentity, subject: string, requestId: string, epoch: number) => revocationRequest(
  `${safeDomainPath(scope)}/subjects/${z.string().uuid().parse(subject)}/revoke`, {
    method: "POST", body: JSON.stringify({ request_id: z.string().uuid().parse(requestId), expected_epoch: z.number().int().nonnegative().parse(epoch) }),
  });
export const getDomainRevocation = (scope: WorkspaceIdentity, identifier: string) => revocationRequest(
  `${safeDomainPath(scope)}/revocations/${z.string().uuid().parse(identifier)}`);

export async function downloadAccessReview(scope: { tenant_id: string; domain_id?: string }): Promise<void> {
  const tenant = z.string().uuid().parse(scope.tenant_id);
  const domain = scope.domain_id === undefined ? undefined : z.string().uuid().parse(scope.domain_id);
  const path = domain ? `${domainPath({ tenant_id: tenant, domain_id: domain })}/access-review.csv` : `/tenants/${tenant}/access-review.csv`;
  const response = await workspaceFetch(`${process.env.NEXT_PUBLIC_API_URL || "/api/v1"}${path}`, {
    method: "GET", credentials: "include", cache: "no-store", headers: { Accept: "text/csv" },
  });
  if (!response.ok) throw new WorkspaceApiError(response.status, "ACCESS_REVIEW_UNAVAILABLE");
  if (!response.headers.get("content-type")?.toLowerCase().startsWith("text/csv")) {
    throw new WorkspaceApiError(502, "INVALID_RESPONSE");
  }
  const prefix = `access-review-${domain ? "domain" : "tenant"}-${domain || tenant}-`;
  const disposition = response.headers.get("content-disposition");
  const provided = disposition?.match(/filename="([^"]+)"/)?.[1];
  if (provided && (!provided.startsWith(prefix) || !/^access-review-(domain|tenant)-[a-f0-9-]+-\d{8}T\d{6}Z\.csv$/.test(provided))) {
    throw new WorkspaceApiError(502, "INVALID_RESPONSE");
  }
  // Cross-origin deployments may not expose Content-Disposition. Preserve a
  // deterministic scope-specific filename without changing shared CORS policy.
  const filename = provided || `${prefix}${new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")}.csv`;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  try {
    link.href = url; link.download = filename; document.body.appendChild(link); link.click();
  } finally {
    link.remove(); URL.revokeObjectURL(url);
  }
}
